'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Bell, CheckCheck, Trash2, X } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer'
import { useAuth } from '@/features/auth/hooks/use-auth'
import {
  useNotificationActions,
  useNotificationsSummary,
} from '@/features/notifications/hooks/use-notifications'
import { useAppSession } from '@/state/session/context'
import type { AppNotificationPreferences, AppNotificationType } from '@/state/session/types'
import { useIsMobile } from '@/hooks/use-mobile'

interface NotificationCenterProps {
  compact?: boolean
  className?: string
}

function isTypeEnabled(type: AppNotificationType, enabled: AppNotificationPreferences) {
  if (!enabled.enabled) return false
  if (type === 'streak-12h' || type === 'streak-4h') return enabled.streakRisk
  if (type === 'mission-claim') return enabled.missions
  if (type === 'achievement-unlocked') return enabled.missions
  if (type === 'boss-open' || type === 'boss-result' || type === 'pet-fainted' || type === 'pet-evolved') return enabled.boss
  if (type === 'level-up') return enabled.levelUp
  if (type === 'duel-invite' || type === 'friend-request' || type === 'challenge-report' || type === 'invite-accepted') {
    return enabled.social
  }
  if (type === 'kaizen-review') return enabled.kaizen
  if (type === 'season-reset' || type === 'year-reset' || type === 'system') return enabled.system
  return true
}

export function NotificationCenter({ compact = false, className }: NotificationCenterProps): React.JSX.Element {
  const router = useRouter()
  const { user } = useAuth()
  const { session } = useAppSession()
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)
  const [readNotificationIds, setReadNotificationIds] = useState<Record<string, true>>({})
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set())
  
  const initialSummary = useMemo(
    () => ({
      items: session.notifications,
      unreadCount: session.notificationsUnreadCount,
      total: session.notificationsTotal,
      previewLimit: session.notificationsPreviewLimit,
    }),
    [
      session.notifications,
      session.notificationsUnreadCount,
      session.notificationsTotal,
      session.notificationsPreviewLimit,
    ],
  )
  const {
    summary,
    refetch: refetchSummary,
  } = useNotificationsSummary(user?.id ?? null, {
    enabled: Boolean(user?.id),
    poll: open,
    initialData: initialSummary,
  })
  const { markAsRead, markAllAsRead: markAllAsReadRemote } = useNotificationActions()

  const summaryItemsKey = useMemo(() => {
    if (summary.items.length === 0) return 'empty:0:0:0:0'
    const first = summary.items[0]
    const last = summary.items[summary.items.length - 1]
    return `${summary.items.length}:${first.id}:${last.id}:${first.read ? '1' : '0'}:${last.read ? '1' : '0'}`
  }, [summary.items.length, summary.items[0]?.id, summary.items[summary.items.length - 1]?.id, summary.items[0]?.read, summary.items[summary.items.length - 1]?.read])

  const notificationPrefsKey = useMemo(
    () => JSON.stringify(session.notificationPreferences),
    [session.notificationPreferences.enabled, session.notificationPreferences.streakRisk, session.notificationPreferences.missions, session.notificationPreferences.boss, session.notificationPreferences.levelUp, session.notificationPreferences.social, session.notificationPreferences.kaizen, session.notificationPreferences.system]
  )

  const deletedIdsKey = useMemo(
    () => Array.from(deletedIds).sort().join(','),
    [deletedIds.size]
  )

  const readNotificationIdsKey = useMemo(
    () => Object.keys(readNotificationIds).sort().join(','),
    [Object.keys(readNotificationIds).length]
  )

  const visibleNotifications = useMemo(
    () =>
      summary.items
        .filter((entry) => !deletedIds.has(entry.id))
        .filter((entry) => isTypeEnabled(entry.type, session.notificationPreferences))
        .map((entry) => ({
          ...entry,
          read: entry.read || Boolean(readNotificationIds[entry.id]),
        }))
        .sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1)),
    [summaryItemsKey, notificationPrefsKey, deletedIdsKey, readNotificationIdsKey]
  )

  const resolvedUnreadIds = new Set(
    summary.items
      .filter((entry) => !entry.read && (Boolean(readNotificationIds[entry.id]) || deletedIds.has(entry.id)))
      .map((entry) => entry.id)
  )
  const unreadCount = Math.max(0, summary.unreadCount - resolvedUnreadIds.size)

  const persistReadStatus = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return
    await Promise.all(ids.map((id) => markAsRead(id)))
    await refetchSummary()
  }, [markAsRead, refetchSummary])

  useEffect(() => {
    if (!open) {
      setReadNotificationIds({})
      setDeletedIds(new Set())
    }
  }, [open])

  const markAllAsRead = useCallback(() => {
    const idsToMark: string[] = []
    setReadNotificationIds((prev) => {
      const next = { ...prev }
      for (const entry of visibleNotifications) {
        next[entry.id] = true
        if (!entry.read) idsToMark.push(entry.id)
      }
      return next
    })
    if (idsToMark.length === 0) return
    void (async () => {
      await markAllAsReadRemote()
      await refetchSummary()
    })()
  }, [visibleNotifications, markAllAsReadRemote, refetchSummary])

  const deleteNotification = useCallback(async (id: string) => {
    setDeletedIds((prev) => new Set(prev).add(id))
    await persistReadStatus([id])
  }, [persistReadStatus])

  const deleteAllRead = useCallback(async () => {
    const readIds = visibleNotifications.filter((n) => n.read).map((n) => n.id)
    if (readIds.length === 0) return
    setDeletedIds((prev) => {
      const next = new Set(prev)
      for (const id of readIds) next.add(id)
      return next
    })
    await persistReadStatus(readIds)
  }, [visibleNotifications, persistReadStatus])

  const handleNotificationClick = useCallback((
    notificationId: string,
    type: AppNotificationType,
    href?: string,
    opensKaizen?: boolean
  ) => {
    setReadNotificationIds((prev) => ({ ...prev, [notificationId]: true }))
    void persistReadStatus([notificationId])

    if (opensKaizen) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('habitpack:open-kaizen-review'))
      }
      setOpen(false)
      return
    }
    if (href) {
      router.push(href)
      setOpen(false)
      return
    }
    if (type === 'mission-claim' || type === 'mission-claimed' || type === 'mission-completed') {
      router.push('/missions')
      setOpen(false)
      return
    }
    if (type === 'achievement-unlocked') { router.push('/dashboard/achievements'); setOpen(false); return }
    if (type === 'season-reset') { router.push('/season-reset'); setOpen(false); return }
    if (type === 'year-reset') { router.push('/year-reset'); setOpen(false) }
  }, [persistReadStatus, router])

  const readCount = visibleNotifications.filter((n) => n.read).length

  const trigger = (
    <button
      type="button"
      className={
        className ??
        'relative flex size-11 items-center justify-center rounded-md border border-border bg-card text-muted-foreground transition-colors hover:text-primary'
      }
      aria-label={unreadCount > 0 ? `Abrir notificações (${unreadCount} não lidas)` : "Abrir notificações"}
    >
      <Bell className="size-4" aria-hidden="true" />
      <span aria-live="polite" aria-atomic="true">
        {unreadCount > 0 ? (
           <span className="absolute -right-1 -top-1 inline-flex min-h-[14px] min-w-[14px] items-center justify-center rounded-full bg-primary px-0.5 text-xs font-black leading-none text-primary-foreground">
            {Math.min(9, unreadCount)}
          </span>
        ) : null}
      </span>
    </button>
  )

  const content = (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-bold text-muted-foreground">
          {unreadCount > 0 ? `${unreadCount} não lida${unreadCount > 1 ? 's' : ''}` : 'Tudo em dia'}
        </p>
        <div className="flex items-center gap-1.5">
          {readCount > 0 && (
            <Button type="button" variant="ghost" size="sm" className="h-11 lg:h-7 gap-1 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={deleteAllRead}>
              <Trash2 className="size-3" /> Apagar lidas
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" className="h-11 lg:h-7 text-xs" onClick={markAllAsRead} disabled={unreadCount === 0}>
            <CheckCheck className="size-3" /> Marcar lidas
          </Button>
        </div>
      </div>

      <div className="max-h-[360px] flex flex-col gap-2 overflow-y-auto pr-1">
        {visibleNotifications.length === 0 ? (
          <p className="rounded-lg border border-border bg-background p-4 text-center text-sm text-muted-foreground">
            Nenhuma notificação ativa.
          </p>
        ) : (
          visibleNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`group relative rounded-lg border px-3 py-3 transition-colors ${
                notification.read
                  ? 'border-border bg-background text-muted-foreground'
                  : 'border-primary/35 bg-primary/10 text-foreground'
              }`}
            >
              <button
                type="button"
                className="w-full text-left"
                onClick={() =>
                  handleNotificationClick(
                    notification.id,
                    notification.type,
                    notification.href,
                    notification.opensKaizen
                  )
                }
              >
                <p className="pr-14 text-sm font-bold leading-tight">{notification.title}</p>
                <p className="mt-0.5 text-xs">{notification.message}</p>
                 <p className="mt-1 text-xs opacity-70">
                  {new Date(notification.createdAt).toLocaleDateString('pt-BR')}{' '}
                  {new Date(notification.createdAt).toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </button>
              <button
                type="button"
                aria-label="Deletar notificação"
                onClick={() => void deleteNotification(notification.id)}
                className="absolute right-2 top-2.5 flex size-11 lg:size-8 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted/60 hover:text-foreground group-hover:opacity-100"
              >
                <X className="size-3.5" aria-hidden="true" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  )

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen} direction="top">
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent>
          <DrawerHeader className="text-left">
            <DrawerTitle>Notificações</DrawerTitle>
            <DrawerDescription className="sr-only">Lista de notificações do sistema</DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-6">{content}</div>
          <div className="mx-auto mb-3 mt-1 h-1.5 w-16 rounded-full bg-muted" />
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Notificações</DialogTitle>
          <DialogDescription className="sr-only">Lista de notificações do sistema</DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  )
}
